import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Mail, Phone, MapPin, CheckCircle2 } from "lucide-react";
import { CONTACT, PACKAGE_OPTIONS } from "../data/content";
import { Reveal, SectionHead } from "./Reveal";
import { ImageUpload } from "./ImageUpload";
import { WhatsAppIcon } from "./WhatsAppButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const initial = { name: "", email: "", phone: "", company: "", package: "beratung", message: "", privacy_accepted: false };

export const Contact = ({ selectedPackage }) => {
  const [form, setForm] = useState(initial);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (selectedPackage) setForm((f) => ({ ...f, package: selectedPackage }));
  }, [selectedPackage]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy_accepted) return toast.error("Bitte akzeptieren Sie die Datenschutzerklärung.");
    if (form.message.trim().length < 10) return toast.error("Bitte beschreiben Sie Ihr Anliegen etwas ausführlicher (mind. 10 Zeichen).");
    if (files.some((f) => f.status === "uploading")) return toast.error("Bitte warten Sie, bis alle Bilder hochgeladen sind.");
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, { ...form, phone: form.phone || null, company: form.company || null, attachments: files.filter((f) => f.id).map((f) => f.id) });
      setDone(true);
      toast.success("Vielen Dank! Ihre Anfrage ist eingegangen.");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Senden fehlgeschlagen. Bitte versuchen Sie es erneut oder schreiben Sie direkt per E-Mail.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kontakt" className="relative py-28 lg:py-40" data-testid="contact-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-8">
        <div className="lg:col-span-5">
          <SectionHead eyebrow="Kontakt" lines={["Lassen Sie Ihr", "Produkt professionell", "wirken."]} text="Beschreiben Sie kurz Ihr Produkt und Ihren Einsatzzweck. Sie erhalten zeitnah ein individuelles Angebot – unverbindlich." />
          <Reveal delay={0.2}>
            <ul className="space-y-5" data-testid="contact-details">
              <li><a href={`mailto:${CONTACT.email}`} className="flex items-center gap-4 group" data-testid="contact-email-link"><span className="w-10 h-10 border border-[var(--line)] flex items-center justify-center text-[var(--gold)] group-hover:bg-[var(--gold)] group-hover:text-[#0a0a0b] transition-colors"><Mail size={16} /></span><span className="text-[var(--text)]">{CONTACT.email}</span></a></li>
              <li><a href={CONTACT.phoneHref} className="flex items-center gap-4 group" data-testid="contact-phone-link"><span className="w-10 h-10 border border-[var(--line)] flex items-center justify-center text-[var(--gold)] group-hover:bg-[var(--gold)] group-hover:text-[#0a0a0b] transition-colors"><Phone size={16} /></span><span className="text-[var(--text)]">{CONTACT.phone}</span></a></li>
              <li><a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group" data-testid="contact-whatsapp-link"><span className="w-10 h-10 border border-[var(--line)] flex items-center justify-center text-[var(--gold)] group-hover:bg-[var(--gold)] group-hover:text-[#0a0a0b] transition-colors"><WhatsAppIcon size={16} /></span><span className="text-[var(--text)]">WhatsApp: {CONTACT.phone}</span></a></li>
              <li className="flex items-center gap-4"><span className="w-10 h-10 border border-[var(--line)] flex items-center justify-center text-[var(--gold)]"><MapPin size={16} /></span><span className="text-[var(--muted)]">{CONTACT.street}, {CONTACT.city}</span></li>
            </ul>
          </Reveal>
        </div>

        <Reveal className="lg:col-span-7" delay={0.15}>
          {done ? (
            <div className="card-glass p-10 lg:p-14 h-full flex flex-col justify-center items-start" data-testid="contact-success">
              <CheckCircle2 size={40} className="text-[var(--gold)]" />
              <h3 className="font-display text-3xl lg:text-4xl mt-6">Anfrage erfolgreich gesendet.</h3>
              <p className="mt-4 text-[var(--muted)] max-w-md">Vielen Dank für Ihr Vertrauen. Eine Eingangsbestätigung mit Ihrer Paketübersicht ist auf dem Weg in Ihr Postfach. Ich melde mich in Kürze persönlich mit einem individuellen Angebot.</p>
              <button onClick={() => { setDone(false); setForm(initial); setFiles([]); }} className="btn-ghost mt-8" data-testid="contact-reset-button">Weitere Anfrage</button>
            </div>
          ) : (
            <form onSubmit={submit} className="card-glass p-8 lg:p-12 grid sm:grid-cols-2 gap-5" data-testid="contact-form" noValidate>
              <div className="sm:col-span-2 flex items-center justify-between">
                <p className="eyebrow">Anfrageformular</p>
                <span className="font-mono text-[0.6rem] text-[var(--muted-2)]">* Pflichtfeld</span>
              </div>
              <input className="field" placeholder="Name *" value={form.name} onChange={set("name")} required data-testid="contact-input-name" />
              <input className="field" type="email" placeholder="E-Mail *" value={form.email} onChange={set("email")} required data-testid="contact-input-email" />
              <input className="field" placeholder="Telefon (optional)" value={form.phone} onChange={set("phone")} data-testid="contact-input-phone" />
              <input className="field" placeholder="Unternehmen / Shop (optional)" value={form.company} onChange={set("company")} data-testid="contact-input-company" />
              <div className="sm:col-span-2">
                <label className="eyebrow !text-[0.6rem] block mb-2">Gewünschtes Paket</label>
                <select className="field" value={form.package} onChange={set("package")} data-testid="contact-select-package">
                  {PACKAGE_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
              </div>
              <textarea className="field sm:col-span-2 min-h-[150px] resize-y" placeholder="Beschreiben Sie Ihr Produkt, den Einsatzzweck (z. B. Amazon Listing, Instagram) und gewünschte Bildanzahl. *" value={form.message} onChange={set("message")} required data-testid="contact-input-message" />
              <ImageUpload files={files} onChange={setFiles} />
              <label className="sm:col-span-2 flex items-start gap-3 text-sm text-[var(--muted)] cursor-pointer">
                <input type="checkbox" checked={form.privacy_accepted} onChange={set("privacy_accepted")} className="mt-1 accent-[#d4af37]" data-testid="contact-checkbox-privacy" />
                <span>Ich habe die <Link to="/datenschutz" className="text-[var(--gold)] underline underline-offset-4">Datenschutzerklärung</Link> gelesen und bin mit der Verarbeitung meiner Daten zur Bearbeitung der Anfrage einverstanden. *</span>
              </label>
              <div className="sm:col-span-2 flex flex-wrap items-center justify-between gap-4 mt-2">
                <p className="text-xs text-[var(--muted-2)]">Antwort in der Regel innerhalb von 24 Stunden.</p>
                <button type="submit" disabled={loading} className="btn-gold" data-testid="contact-submit-button">
                  {loading ? "Wird gesendet…" : "Anfrage senden"} <ArrowUpRight size={16} />
                </button>
              </div>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
